import React, { useState, useEffect, useMemo } from 'react';
import { Play, ShoppingCart, Filter, Music, Grid3X3, List, ChevronLeft, ChevronRight, PackageCheck } from 'lucide-react';
import { genres, moods, bpmRanges } from '../mock';
import { useAudio } from '../context/AudioContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = '';
const COMPACT_ITEMS_PER_PAGE = 12;
const COMPACT_COLUMNS = 2;
const COMPACT_ITEMS_PER_COLUMN = COMPACT_ITEMS_PER_PAGE / COMPACT_COLUMNS;
const GRID_ITEMS_PER_PAGE = 12;

const BeatStore = () => {
  const { playTrack, currentTrack, isPlaying } = useAudio();
  const { addToCart } = useCart();
  const { currentTheme } = useTheme();
  
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [selectedMood, setSelectedMood] = useState('All');
  const [selectedBPM, setSelectedBPM] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [compactPage, setCompactPage] = useState(1);
  const [gridPage, setGridPage] = useState(1);

  useEffect(() => {
    loadBeats();
  }, []);

  useEffect(() => {
    setCompactPage(1);
    setGridPage(1);
  }, [selectedGenre, selectedMood, selectedBPM, searchQuery]);

  const loadBeats = async () => {
    try {
      const response = await axios.get(`/api/beats`);
      setBeats(response.data);
    } catch (error) {
      console.error('Failed to load beats:', error);
      setBeats([]);
    } finally {
      setLoading(false);
    }
  };

  const parseMultiValueField = (value) => {
    if (!value) return [];
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getPageNumbers = (currentPage, totalPages) => {
    if (totalPages <= 1) return [1];

    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const safePages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    return safePages;
  };

  const availableGenres = useMemo(() => {
    const dynamicGenres = beats.flatMap((beat) => parseMultiValueField(beat.genre));
    return ['All', ...Array.from(new Set(dynamicGenres)).sort((a, b) => a.localeCompare(b))];
  }, [beats]);

  const availableMoods = useMemo(() => {
    const dynamicMoods = beats.flatMap((beat) => parseMultiValueField(beat.mood));
    return ['All', ...Array.from(new Set(dynamicMoods)).sort((a, b) => a.localeCompare(b))];
  }, [beats]);

  const filteredBeats = useMemo(() => {
    return beats.filter(beat => {
      const beatGenres = parseMultiValueField(beat.genre).map((item) => item.toLowerCase());
      const beatMoods = parseMultiValueField(beat.mood).map((item) => item.toLowerCase());
      const beatGenreText = beatGenres.join(' ');
      const beatMoodText = beatMoods.join(' ');
      
      const matchesGenre = selectedGenre === 'All' || beatGenres.includes(selectedGenre.toLowerCase());
      const matchesMood = selectedMood === 'All' || beatMoods.includes(selectedMood.toLowerCase());
      
      let matchesBPM = true;
      if (selectedBPM !== 'All') {
        const [min, max] = selectedBPM.includes('+') 
          ? [parseInt(selectedBPM), 999] 
          : selectedBPM.split('-').map(Number);
        matchesBPM = beat.bpm >= min && beat.bpm <= max;
      }
      
      const matchesSearch = !searchQuery || 
                           (beat.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                           beatGenreText.includes(searchQuery.toLowerCase()) ||
                           beatMoodText.includes(searchQuery.toLowerCase());
      
      return matchesGenre && matchesMood && matchesBPM && matchesSearch;
    });
  }, [beats, selectedGenre, selectedMood, selectedBPM, searchQuery]);

  const compactTotalPages = Math.max(1, Math.ceil(filteredBeats.length / COMPACT_ITEMS_PER_PAGE));
  const safeCompactPage = Math.min(compactPage, compactTotalPages);
  const compactPageNumbers = getPageNumbers(safeCompactPage, compactTotalPages);
  const compactPageBeats = useMemo(() => {
    const start = (safeCompactPage - 1) * COMPACT_ITEMS_PER_PAGE;
    return filteredBeats.slice(start, start + COMPACT_ITEMS_PER_PAGE);
  }, [filteredBeats, safeCompactPage]);

  const compactColumns = useMemo(() => {
    return Array.from({ length: COMPACT_COLUMNS }, (_, index) => {
      const start = index * COMPACT_ITEMS_PER_COLUMN;
      return compactPageBeats.slice(start, start + COMPACT_ITEMS_PER_COLUMN);
    });
  }, [compactPageBeats]);

  const gridTotalPages = Math.max(1, Math.ceil(filteredBeats.length / GRID_ITEMS_PER_PAGE));
  const safeGridPage = Math.min(gridPage, gridTotalPages);
  const gridPageNumbers = getPageNumbers(safeGridPage, gridTotalPages);
  const gridPageBeats = useMemo(() => {
    const start = (safeGridPage - 1) * GRID_ITEMS_PER_PAGE;
    return filteredBeats.slice(start, start + GRID_ITEMS_PER_PAGE);
  }, [filteredBeats, safeGridPage]);

  const getAssetUrl = (path) => {
    if (!path) return null;
    return path.startsWith('/uploads') ? path : `${BACKEND_URL}${path}`;
  };

  const hasMp3 = (beat) => Boolean(beat?.mp3Url);
  const hasWav = (beat) => Boolean(beat?.wavUrl);
  const hasPreviewAudio = (beat) => hasMp3(beat) || hasWav(beat);
  const hasCoverArt = (beat) => Boolean(beat?.imageUrl);
  const hasTrackouts = (beat) => Boolean(beat?.trackoutZipUrl || beat?.trackoutsUrl || beat?.stemsUrl || beat?.zipUrl);

  const requiresTrackouts = (license) => {
    if (!license) return false;

    const licenseName = (license.name || '').toLowerCase();
    const description = (license.description || '').toLowerCase();
    const combined = `${licenseName} ${description}`;
    const numericPrice = typeof license.price === 'number' ? license.price : parseFloat(license.price);

    return (
      combined.includes('trackout') ||
      combined.includes('track out') ||
      combined.includes('stems') ||
      combined.includes('unlimited') ||
      numericPrice === 99.99
    );
  };

  const isLicensePurchasable = (beat, license) => {
    if (!license || typeof license.price !== 'number') return false;
    if (!hasPreviewAudio(beat) || !hasCoverArt(beat)) return false;
    if (requiresTrackouts(license) && !hasTrackouts(beat)) return false;
    return true;
  };

  const getVisibleLicenses = (beat) => {
    const licenses = Array.isArray(beat?.licenses) ? beat.licenses : [];
    return licenses.filter((license) => {
      if (typeof license?.price !== 'number') return true;
      if (!hasPreviewAudio(beat) || !hasCoverArt(beat)) return false;
      if (requiresTrackouts(license) && !hasTrackouts(beat)) return false;
      return true;
    });
  };

  const getPrimaryVisibleLicense = (beat) => {
    const visibleLicenses = getVisibleLicenses(beat);
    return visibleLicenses.find((license) => typeof license?.price === 'number') || visibleLicenses[0] || null;
  };

  const isBeatReadyForSale = (beat) => {
    return getVisibleLicenses(beat).some((license) => isLicensePurchasable(beat, license));
  };

  const isBeatFullyPackaged = (beat) => {
    return isBeatReadyForSale(beat) && hasMp3(beat) && hasWav(beat) && hasTrackouts(beat);
  };

  const handleAddToCart = (beat, selectedLicense = null) => {
    const license = selectedLicense || getPrimaryVisibleLicense(beat);

    if (!license) {
      toast({
        title: 'No licensing available',
        description: "This beat doesn't have pricing information.",
        variant: 'destructive'
      });
      return;
    }

    if (!isLicensePurchasable(beat, license)) {
      toast({
        title: 'License not ready',
        description: 'That license needs files that have not been uploaded yet.',
        variant: 'destructive'
      });
      return;
    }
    
    const beatItem = {
      ...beat,
      type: 'beat',
      selectedLicense: license,
      price: typeof license.price === 'number' ? license.price : 0,
      audioUrl: getAssetUrl(beat.mp3Url || beat.wavUrl),
      imageUrl: getAssetUrl(beat.imageUrl)
    };
    
    addToCart(beatItem);
    toast({
      title: 'Added to cart',
      description: `${beat.title} (${license.name}) has been added to your cart.`,
    });
  };

  const handlePlayBeat = (beat) => {
    const beatWithUrls = {
      ...beat,
      audioUrl: getAssetUrl(beat.mp3Url || beat.wavUrl),
      imageUrl: getAssetUrl(beat.imageUrl)
    };

    const allPlayable = filteredBeats
      .filter(b => b.mp3Url || b.wavUrl)
      .map(b => ({
        ...b,
        audioUrl: getAssetUrl(b.mp3Url || b.wavUrl),
        imageUrl: getAssetUrl(b.imageUrl)
      }));

    playTrack(beatWithUrls, allPlayable);
  };

  const renderCompactBeatRow = (beat) => {
    const primaryLicense = getPrimaryVisibleLicense(beat);
    const readyForSale = isBeatReadyForSale(beat);
    const fullyPackaged = isBeatFullyPackaged(beat);

    return (
      <div 
        key={beat.id}
        className="compact-column-row group flex items-center gap-3 bg-zinc-900/60 hover:bg-zinc-800/80 rounded-xl px-4 py-3 transition-all border border-white/5 hover:border-white/10"
      >
        <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
          <img 
            src={getAssetUrl(beat.imageUrl) || 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100&h=100&fit=crop'} 
            alt={beat.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100&h=100&fit=crop'; }}
          />
          <button
            onClick={() => handlePlayBeat(beat)}
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {currentTrack?.id === beat.id && isPlaying ? (
              <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: currentTheme.primary }} />
            ) : (
              <Play className="w-5 h-5" style={{ color: currentTheme.primary }} />
            )}
          </button>
        </div>

        <div className="flex-1 min-w-0 grid grid-cols-1 xl:grid-cols-12 gap-1 xl:gap-3 items-center">
          <div className="xl:col-span-4">
            <p className="text-white font-semibold text-sm truncate">{beat.title}</p>
          </div>
          <div className="xl:col-span-2 hidden xl:block">
            <span className="text-gray-400 text-xs">{beat.genre || '—'}</span>
          </div>
          <div className="xl:col-span-1 hidden xl:block">
            <span className="text-gray-400 text-xs">{beat.bpm || '—'}</span>
          </div>
          <div className="xl:col-span-1 hidden xl:block">
            <span className="text-gray-400 text-xs">{beat.key || '—'}</span>
          </div>
          <div className="xl:col-span-2 hidden xl:block">
            <span className="text-gray-400 text-xs truncate block">{beat.mood || '—'}</span>
          </div>
          <div className="xl:hidden flex gap-2 text-xs text-gray-500 flex-wrap">
            {beat.genre && <span>{beat.genre}</span>}
            {beat.bpm && <span>• {beat.bpm} BPM</span>}
            {beat.key && <span>• {beat.key}</span>}
          </div>
          <div className="xl:col-span-2 flex items-center justify-end gap-2">
            {primaryLicense && typeof primaryLicense.price === 'number' ? (
              <>
                <span className="text-white font-bold text-sm">
                  ${primaryLicense.price}
                </span>
                {fullyPackaged && (
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-black bg-emerald-400">
                    <PackageCheck className="w-3 h-3" />
                    Ready
                  </span>
                )}
                {readyForSale && (
                  <Button
                    size="sm"
                    onClick={() => handleAddToCart(beat, primaryLicense)}
                    className="text-black font-bold text-xs hover:scale-105 transition-transform"
                    style={{ background: 'linear-gradient(135deg, #2ecc71, #27ae60)' }}
                  >
                    <ShoppingCart className="w-3 h-3 mr-1" />
                    Buy
                  </Button>
                )}
              </>
            ) : (
              <span className="text-gray-500 text-xs">No pricing</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPageButtons = (pageNumbers, currentPage, setPage) => {
    return pageNumbers.map((page, index) => {
      const previousPage = pageNumbers[index - 1];
      const showGap = previousPage && page - previousPage > 1;

      return (
        <React.Fragment key={`page-${page}`}>
          {showGap && <span className="text-gray-500 px-1">…</span>}
          <button
            type="button"
            onClick={() => setPage(page)}
            className="min-w-[36px] h-9 px-3 rounded-lg border text-sm font-semibold transition-all"
            style={
              currentPage === page
                ? { backgroundColor: currentTheme.primary, color: '#000', borderColor: currentTheme.primary }
                : { backgroundColor: 'rgba(0,0,0,0.4)', color: '#d1d5db', borderColor: 'rgba(255,255,255,0.1)' }
            }
          >
            {page}
          </button>
        </React.Fragment>
      );
    });
  };

  const gridViewClasses = viewMode === 'grid' ? 'beatstars-grid' : 'hidden';
  const listViewClasses = viewMode === 'compact' ? 'space-y-4' : 'hidden';

  return (
    <div className="min-h-screen bg-black pt-24 pb-32">
      <div className="max-w-[1600px] mx-auto px-4 md:px-5">
        <div className="mb-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Beat Store</h1>
          <p className="text-lg md:text-xl text-gray-400">Browse our collection of premium hip-hop instrumentals</p>
        </div>

        <div className="mb-6 p-5 rounded-2xl bg-zinc-900/50 backdrop-blur-sm border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5" style={{ color: currentTheme.primary }} />
            <h2 className="text-white font-bold text-lg">Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-gray-400 text-xs font-semibold uppercase tracking-[0.18em] mb-2 block">Search</label>
              <input
                type="text"
                placeholder="Search beats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all"
                style={{ '--tw-ring-color': currentTheme.primary }}
              />
            </div>
            
            <div>
              <label className="text-gray-400 text-xs font-semibold uppercase tracking-[0.18em] mb-2 block">Genre</label>
              <Select value={selectedGenre} onValueChange={setSelectedGenre}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Genre" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {availableGenres.map(genre => (
                    <SelectItem key={genre} value={genre} className="text-white hover:bg-white/5">
                      {genre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-gray-400 text-xs font-semibold uppercase tracking-[0.18em] mb-2 block">Mood</label>
              <Select value={selectedMood} onValueChange={setSelectedMood}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Mood" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {availableMoods.map(mood => (
                    <SelectItem key={mood} value={mood} className="text-white hover:bg-white/5">
                      {mood}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-gray-400 text-xs font-semibold uppercase tracking-[0.18em] mb-2 block">BPM</label>
              <Select value={selectedBPM} onValueChange={setSelectedBPM}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="BPM" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {bpmRanges.map(range => (
                    <SelectItem key={range} value={range} className="text-white hover:bg-white/5">
                      {range} BPM
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="mb-5 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-gray-400 text-sm md:text-base">
            Showing <span className="text-white font-bold">{filteredBeats.length}</span> beats
          </p>
          <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-white/10">
            <button
              onClick={() => setViewMode('grid')}
              className="p-2 rounded-md transition-all"
              style={{ 
                backgroundColor: viewMode === 'grid' ? currentTheme.primary : 'transparent',
                color: viewMode === 'grid' ? '#000' : '#9ca3af'
              }}
              title="Grid view"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('compact')}
              className="p-2 rounded-md transition-all"
              style={{ 
                backgroundColor: viewMode === 'compact' ? currentTheme.primary : 'transparent',
                color: viewMode === 'compact' ? '#000' : '#9ca3af'
              }}
              title="Compact view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-gray-600 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-gray-400 text-xl">Loading beats...</p>
          </div>
        ) : beats.length === 0 ? (
          <div className="text-center py-20">
            <Music className="w-24 h-24 text-gray-600 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-white mb-4">No Beats Available Yet</h3>
            <p className="text-gray-400 text-lg mb-8">New beats will appear here once they're uploaded.</p>
            <p className="text-gray-500 text-sm">Check back soon for exclusive production!</p>
          </div>
        ) : (
          <>
            <div className={listViewClasses}>
              <div className="compact-column-window rounded-2xl bg-zinc-900/50 border border-white/10 p-4 md:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-white font-bold text-xl">Beat List</h2>
                    <p className="text-gray-400 text-sm">
                      Two-column browsing window with its own scroll area and page flip controls.
                    </p>
                  </div>

                  {filteredBeats.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompactPage(prev => Math.max(1, prev - 1))}
                        disabled={safeCompactPage === 1}
                        className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Prev
                      </Button>
                      {renderPageButtons(compactPageNumbers, safeCompactPage, setCompactPage)}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompactPage(prev => Math.min(compactTotalPages, prev + 1))}
                        disabled={safeCompactPage === compactTotalPages}
                        className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="hidden xl:grid xl:grid-cols-2 gap-4 px-4 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-white/10 mb-4">
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-1"></div>
                    <div className="col-span-4">Title</div>
                    <div className="col-span-2">Genre</div>
                    <div className="col-span-1">BPM</div>
                    <div className="col-span-1">Key</div>
                    <div className="col-span-2">Mood</div>
                    <div className="col-span-2 text-right">Price</div>
                  </div>
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-1"></div>
                    <div className="col-span-4">Title</div>
                    <div className="col-span-2">Genre</div>
                    <div className="col-span-1">BPM</div>
                    <div className="col-span-1">Key</div>
                    <div className="col-span-2">Mood</div>
                    <div className="col-span-2 text-right">Price</div>
                  </div>
                </div>

                {filteredBeats.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-gray-400 text-xl">No beats found matching your filters</p>
                    <p className="text-gray-500 text-sm mt-2">Try adjusting your search criteria</p>
                  </div>
                ) : (
                  <div className="compact-column-grid">
                    {compactColumns.map((column, columnIndex) => (
                      <div
                        key={`compact-column-${columnIndex}`}
                        className="rounded-2xl bg-black/30 border border-white/10 p-3 compact-column-scroll min-h-[620px] max-h-[620px] overflow-y-auto"
                      >
                        <div className="space-y-2">
                          {column.length > 0 ? (
                            column.map(renderCompactBeatRow)
                          ) : (
                            <div className="compact-column-empty h-full min-h-[560px] flex items-center justify-center text-sm text-gray-500 border border-dashed border-white/10 rounded-xl">
                              No more beats on this side
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {viewMode === 'grid' && filteredBeats.length > 0 && (
              <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGridPage(prev => Math.max(1, prev - 1))}
                  disabled={safeGridPage === 1}
                  className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Prev
                </Button>
                {renderPageButtons(gridPageNumbers, safeGridPage, setGridPage)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGridPage(prev => Math.min(gridTotalPages, prev + 1))}
                  disabled={safeGridPage === gridTotalPages}
                  className="border-white/10 text-white hover:bg-white/5 disabled:opacity-40"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            <div className={`${gridViewClasses} beatstars-grid-mobile-compact`}>
              {gridPageBeats.map(beat => {
                const visibleLicenses = getVisibleLicenses(beat);
                const primaryLicense = getPrimaryVisibleLicense(beat);
                const readyForSale = isBeatReadyForSale(beat);
                const fullyPackaged = isBeatFullyPackaged(beat);
                const lowestPrice = typeof primaryLicense?.price === 'number' ? `$${primaryLicense.price}` : null;

                return (
                  <div 
                    key={beat.id}
                    className="beat-card group rounded-2xl overflow-hidden transition-all duration-300"
                  >
                    <div className="beat-card-cover relative aspect-square overflow-hidden">
                      <img 
                        src={getAssetUrl(beat.imageUrl) || 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=500&h=500&fit=crop'} 
                        alt={beat.title}
                        className="w-full h-full object-cover beat-card-image"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=500&h=500&fit=crop';
                        }}
                      />

                      <div className="beat-card-overlay absolute inset-0" />

                      {lowestPrice && (
                        <div className="absolute top-3 left-3 z-20">
                          <span className="beat-price-badge px-3 py-1.5 rounded-full text-xs font-bold text-black">
                            {lowestPrice}
                          </span>
                        </div>
                      )}

                      {beat.key && (
                        <div className="absolute top-3 right-3 z-20">
                          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-black/65 backdrop-blur-sm text-white border border-white/15">
                            {beat.key}
                          </span>
                        </div>
                      )}
                      
                      <button
                        onClick={() => handlePlayBeat(beat)}
                        className="beat-play-button absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-300"
                        aria-label={`Play ${beat.title}`}
                      >
                        <div
                          className="w-16 h-16 md:w-18 md:h-18 rounded-full flex items-center justify-center backdrop-blur-md bg-black/45 border transition-all duration-300"
                          style={{ borderColor: `${currentTheme.primary}99` }}
                        >
                          {currentTrack?.id === beat.id && isPlaying ? (
                            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: currentTheme.primary }} />
                          ) : (
                            <Play className="w-7 h-7 ml-1" style={{ color: currentTheme.primary }} />
                          )}
                        </div>
                      </button>
                    </div>

                    <div className="p-3 md:p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <h3 className="text-white font-bold text-base md:text-lg truncate">{beat.title}</h3>
                          <p className="text-gray-400 text-xs md:text-sm truncate">
                            {[beat.genre, beat.mood].filter(Boolean).join(' • ') || 'Premium instrumental'}
                          </p>
                        </div>
                        {fullyPackaged && (
                          <div className="flex items-center gap-1 text-[11px] text-emerald-400 shrink-0 mt-1">
                            <PackageCheck className="w-3.5 h-3.5" />
                            Ready
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {beat.bpm && (
                          <span className="px-2 py-1 rounded-md text-[11px] font-medium bg-white/8 text-gray-300 border border-white/10">
                            {beat.bpm} BPM
                          </span>
                        )}
                        {beat.key && (
                          <span className="px-2 py-1 rounded-md text-[11px] font-medium bg-white/8 text-gray-300 border border-white/10">
                            {beat.key}
                          </span>
                        )}
                        {visibleLicenses.slice(0, 2).map((license, idx) => (
                          <span key={`${beat.id}-license-chip-${idx}`} className="px-2 py-1 rounded-md text-[11px] font-medium bg-white/8 text-gray-300 border border-white/10 truncate max-w-full">
                            {license.name}
                          </span>
                        ))}
                      </div>

                      {visibleLicenses.length > 0 ? (
                        <div className="space-y-2 mb-3">
                          {visibleLicenses.map((license, idx) => {
                            const purchasable = isLicensePurchasable(beat, license);
                            return (
                              <div key={`${beat.id}-license-${idx}`} className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-xl bg-black/30 border border-white/10 hover:border-white/20 transition-all">
                                <div className="min-w-0 flex-1">
                                  <p className="text-white text-sm font-semibold truncate">{license.name}</p>
                                  {license.description && (
                                    <p className="text-gray-400 text-xs truncate">{license.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-white font-bold text-sm">
                                    {typeof license.price === 'number' ? `$${license.price}` : license.price}
                                  </span>
                                  {purchasable && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleAddToCart(beat, license)}
                                      className="beat-buy-button text-black font-bold px-3 shrink-0"
                                      style={{ background: 'linear-gradient(135deg, #2ecc71, #27ae60, #2ecc71, #16a085)' }}
                                    >
                                      <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                                      Buy
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-white/10 bg-black/20 px-3 py-4 mb-3 text-center">
                          <p className="text-gray-400 font-semibold text-sm">No live license tiers</p>
                          <p className="text-gray-500 text-xs mt-1">Add pricing and preview assets to activate sales</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          {lowestPrice ? (
                            <>
                              <p className="text-white font-bold text-lg leading-none">{lowestPrice}</p>
                              <p className="text-gray-500 text-[11px] mt-1">Starting price</p>
                            </>
                          ) : (
                            <>
                              <p className="text-gray-400 font-semibold text-sm leading-none">No price set</p>
                              <p className="text-gray-500 text-[11px] mt-1">Set a license price</p>
                            </>
                          )}
                        </div>

                        {fullyPackaged && (
                          <div className="text-right shrink-0">
                            <p className="text-emerald-400 text-[11px] font-semibold">Ready & packaged</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredBeats.length === 0 && beats.length > 0 && (
              <div className="text-center py-20">
                <p className="text-gray-400 text-xl">No beats found matching your filters</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your search criteria</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BeatStore;
